import '@mantine/core/styles.css';
import '@mantine/charts/styles.css';

import { MantineProvider } from '@mantine/core';
import { Worker } from '@react-pdf-viewer/core';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { useHydrateAtoms } from 'jotai/react/utils';
import { queryClientAtom } from 'jotai-tanstack-query';
import { PropsWithChildren, useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { TouchBackend } from 'react-dnd-touch-backend';
import { PageLoadMonitor } from 'src/components/common/PageLoadMonitor';
import { RootErrorBoundary } from 'src/components/common/RootErrorBoundary';
import { AppContextProvider, useAppContext } from 'src/contexts/AppContext';
import { AppPerformanceContextProvider } from 'src/contexts/AppPerformanceContext';
import { BulkEditHubContextProvider } from 'src/contexts/BulkEditHubContext';
import { ClientLogContextProvider } from 'src/contexts/ClientLogContext';
import { SiteContentProvider } from 'src/contexts/ContentContext';
import { DialogProvider } from 'src/contexts/DialogContext/DialogContext';
import { EmailPurchaseOrderContextProvider } from 'src/contexts/EmailPurchaseOrderContext/EmailPurchaseOrderContext';
import { FileExportContextProvider } from 'src/contexts/FileExportContext';
import { GenericDialogProvider } from 'src/contexts/GenericDialogContext/GenericDialogContext';
import { LocalizationContextProvider } from 'src/contexts/LocalizationContext';
import { NotificationsContextProvider } from 'src/contexts/NotificationsContext';
import { PageLoadStatusProvider } from 'src/contexts/PageLoadStatusContext';
import { ScreenSizeProvider } from 'src/contexts/ScreenSizeContext/ScreenSizeContext';
import { SellerAccountContextProvider } from 'src/contexts/SellerAccountContext';
import { SellerRoleContextProvider } from 'src/contexts/SellerRoleContext';
import { SellerUserAllSettingsProvider } from 'src/contexts/SellerUserSettingsContext/SellerUserAllSettingsContext';
import {
  SiteThemeContextProvider,
  useSiteTheme,
} from 'src/contexts/SiteTheme/SiteThemeContext';
import { SiteTimezoneContextProvider } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { ThemeUtils } from 'src/core/themes';
import { MainRoute } from 'src/navigations/Routes/MainRoute';
import { RootErrorPage } from 'src/views';
import { DefaultTheme, ThemeProvider } from 'styled-components';

import * as styles from './App.css';
import { AppContent } from './AppContent';
import { MainQueryClient } from './QueryClients';

const touchBackendOptions = { enableMouseEvents: true };

function AppLayout() {
  const { appContext, cobrandId, mainRoute } = useAppContext();
  const { isDarkMode } = useSiteTheme();
  const [legacyTheme, setLegacyTheme] = useState<DefaultTheme>();

  const [isThemeLoading, setIsThemeLoading] = useState(true);

  useEffect(() => {
    const loadTheme = async () => {
      setIsThemeLoading(true);
      try {
        await ThemeUtils.adjustBodyThemeClasses(cobrandId, isDarkMode);
        const theme = await ThemeUtils.getLegacyThemeForCoBrand(
          cobrandId,
          isDarkMode
        );
        setLegacyTheme(theme);
      } catch (error) {
        console.error('Failed to load theme:', error);
        // Set a fallback theme if loading fails
        const fallbackTheme = await ThemeUtils.loadLegacyTheme(
          'reachPro'
        ).catch(() => ({}) as DefaultTheme);
        setLegacyTheme(fallbackTheme);
      } finally {
        setIsThemeLoading(false);
      }
    };

    loadTheme();
  }, [cobrandId, isDarkMode]);

  if (!appContext || isThemeLoading || !legacyTheme) {
    return (
      <div className={styles.spinnerContainer}>
        <PosSpinner />
      </div>
    );
  }

  return (
    <ThemeProvider theme={legacyTheme}>
      <MantineProvider theme={{ fontFamily: "'Inter', sans-serif" }}>
        <ScreenSizeProvider>
          <DialogProvider>
            <GenericDialogProvider>
              <ClientLogContextProvider>
                <PageLoadStatusProvider>
                  <AppPerformanceContextProvider>
                    {/* Everthing below depends on the error-boundery context */}
                    <SiteContentProvider>
                      <SellerUserAllSettingsProvider>
                        <SiteTimezoneContextProvider>
                          <LocalizationContextProvider>
                            <SellerAccountContextProvider>
                              <SellerRoleContextProvider>
                                <EmailPurchaseOrderContextProvider>
                                  {/* See https://github.com/react-dnd/react-dnd/issues/3257#issuecomment-1239254032 */}
                                  <DndProvider
                                    backend={TouchBackend}
                                    options={touchBackendOptions}
                                  >
                                    <NotificationsContextProvider
                                      loadActiveNotifications={
                                        mainRoute === MainRoute.Home ||
                                        mainRoute === MainRoute.Notifications
                                      }
                                    >
                                      <FileExportContextProvider>
                                        <BulkEditHubContextProvider>
                                          <DialogProvider>
                                            <GenericDialogProvider>
                                              {/* Required for pdf viewer to work https://react-pdf-viewer.dev/docs/basic-usage/ */}
                                              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                                                <AppContent />
                                              </Worker>
                                            </GenericDialogProvider>
                                          </DialogProvider>
                                        </BulkEditHubContextProvider>
                                      </FileExportContextProvider>
                                    </NotificationsContextProvider>
                                  </DndProvider>
                                </EmailPurchaseOrderContextProvider>
                              </SellerRoleContextProvider>
                            </SellerAccountContextProvider>
                          </LocalizationContextProvider>
                          <PageLoadMonitor />
                        </SiteTimezoneContextProvider>
                      </SellerUserAllSettingsProvider>
                    </SiteContentProvider>
                  </AppPerformanceContextProvider>
                </PageLoadStatusProvider>
              </ClientLogContextProvider>
            </GenericDialogProvider>
          </DialogProvider>
        </ScreenSizeProvider>
      </MantineProvider>
    </ThemeProvider>
  );
}

function AppWithErrorBoundary() {
  const { activeAccountWebClientConfig } = useAppContext();

  return (
    <RootErrorBoundary
      webClientConfig={activeAccountWebClientConfig}
      fallback={RootErrorPage}
    >
      <SiteThemeContextProvider>
        <AppLayout />
      </SiteThemeContextProvider>
    </RootErrorBoundary>
  );
}

const HydrateAtoms = ({ children }: PropsWithChildren) => {
  const queryClient = useQueryClient();
  useHydrateAtoms([[queryClientAtom, queryClient]]);
  return children;
};

function App() {
  return (
    <QueryClientProvider {...MainQueryClient}>
      <HydrateAtoms>
        <AppContextProvider>
          <AppWithErrorBoundary />
        </AppContextProvider>
      </HydrateAtoms>
    </QueryClientProvider>
  );
}

export default App;
